import { useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { createPortal } from "react-dom";

export const input =
  "w-full rounded-lg border border-neutral-300 bg-white px-2.5 py-1.5 text-sm shadow-sm outline-none transition-colors focus:border-accent-500 focus:ring-2 focus:ring-accent-500/25 disabled:bg-neutral-100 disabled:shadow-none";

export function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  // | undefined is explicit because exactOptionalPropertyTypes is on:
  // callers pass error={maybeUndefined} rather than omitting the prop.
  error?: string | undefined;
  hint?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500">
        {label}
      </span>
      {children}
      {/* Errors replace hints rather than stacking — two lines of guidance
          under one input is noise at the moment someone is trying to fix it. */}
      {error ? (
        <span className="mt-1 block text-xs text-red-600">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-neutral-500">{hint}</span>
      ) : null}
    </label>
  );
}

/**
 * A percentage, typed as a percentage.
 *
 * Rates are stored 0–1 and every calculation expects that form, but a form
 * field is the one place the two forms meet and the operator's is the one that
 * wins: you type 50 for half. The sign sits inside the field rather than in a
 * hint underneath, so there is no reading of the box that gets the unit wrong.
 *
 * The state stays the string as typed — half-finished values like "12." and
 * "0" survive — and callers convert with `rateFromPercent` on submit.
 */
export function PercentInput({
  value,
  onChange,
  ...rest
}: {
  value: string;
  onChange: (next: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">) {
  return (
    <div className="relative">
      <input
        {...rest}
        className={`${input} pr-7`}
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-sm text-neutral-400">
        %
      </span>
    </div>
  );
}

export function Button({
  children,
  variant = "primary",
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "ghost" }) {
  const base =
    "rounded-lg border border-transparent px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const styles =
    variant === "primary"
      ? "bg-accent-600 text-white shadow-sm hover:bg-accent-500"
      : "text-neutral-600 hover:bg-accent-50 hover:text-accent-700";
  return (
    <button type="button" {...rest} className={`${base} ${styles} ${rest.className ?? ""}`}>
      {children}
    </button>
  );
}

/**
 * A row's expand toggle — for detail that belongs to one row rather than a
 * column of its own, so opening it doesn't cost a trip off the table.
 */
export function ChevronButton({
  open,
  onClick,
  label,
}: {
  open: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-label={`${open ? "Hide" : "Show"} ${label}`}
      title={`${open ? "Hide" : "Show"} ${label}`}
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 16 16"
        fill="none"
        className={`transition-transform ${open ? "rotate-90" : ""}`}
      >
        <path
          d="M6 4l4 4-4 4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/**
 * A styled tooltip anchored to `trigger`, positioned via `getBoundingClientRect`
 * and rendered through a portal into `document.body` — not as a CSS-only
 * `absolute` child of the trigger. A plain absolute popover gets clipped the
 * moment its trigger sits inside any scrolling ancestor (a `Table`'s
 * `overflow-auto` wrapper, for one), since the scrollable-overflow region of
 * an `overflow: auto` box includes positioned descendants that extend past
 * it. Escaping to `document.body` with `position: fixed` sidesteps that
 * entirely. Read-only by design — `pointer-events-none`, shown on hover or
 * keyboard focus, gone the instant either ends. Anything that needs to stay
 * open for a click (a form, a thread you're replying in) belongs in normal
 * page flow instead, not in here.
 */
export function HoverCard({
  trigger,
  children,
}: {
  trigger: React.ReactNode;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  function show() {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 8, left: r.left + r.width / 2 });
  }
  function hide() {
    setPos(null);
  }

  return (
    <span
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      className="inline-block"
    >
      {trigger}
      {pos &&
        createPortal(
          <div
            role="tooltip"
            style={{ top: pos.top, left: pos.left }}
            className="pointer-events-none fixed z-50 w-72 -translate-x-1/2 rounded-lg border border-neutral-200 bg-white p-3 text-left text-xs shadow-lg"
          >
            {children}
          </div>,
          document.body,
        )}
    </span>
  );
}

/**
 * Delete, with the confirmation built into the button rather than a dialog.
 *
 * Two clicks, and the second one says what it is about to do. A modal asking
 * "are you sure?" is dismissed on reflex; a button that changes under the
 * cursor is read. It disarms itself after a few seconds so a stray first click
 * doesn't leave a live delete waiting on the page.
 *
 * Only rendered for owners — but the API is what enforces that, not this.
 */
export function DeleteButton({
  onConfirm,
  label = "Delete",
  busy,
}: {
  onConfirm: () => void;
  label?: string | undefined;
  busy?: boolean | undefined;
}) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);

  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        if (!armed) {
          setArmed(true);
          return;
        }
        setArmed(false);
        onConfirm();
      }}
      className={`rounded-lg border border-transparent px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        armed
          ? "bg-red-600 text-white shadow-sm hover:bg-red-500"
          : "text-neutral-500 hover:bg-red-50 hover:text-red-700"
      }`}
    >
      {armed ? "Delete for good?" : label}
    </button>
  );
}

/**
 * A focused form over the page rather than in it — for an action tied to one
 * spot (a chip, a row) where pushing the surrounding layout down would separate
 * the trigger from what it opened.
 *
 * A native `<dialog>` rather than a styled div: `showModal()` gives us Escape-
 * to-close, focus trapping, and inert content behind it for free, instead of
 * reimplementing each by hand.
 */
export function Modal({
  title,
  wide = false,
  onClose,
  children,
}: {
  title: string;
  /** For a multi-column form, whose grid needs more than a confirmation does. */
  wide?: boolean | undefined;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: mouse-only backdrop dismissal — Escape already closes the dialog natively, so a matching key handler here would just duplicate that.
    <dialog
      ref={ref}
      aria-label={title}
      onClose={onClose}
      onClick={(e) => {
        // A click that lands on the dialog element itself (rather than
        // something inside it) is a click on the backdrop — the content box
        // doesn't cover the full viewport, so anything outside it bubbles up.
        if (e.target === ref.current) onClose();
      }}
      className={
        // `m-auto` is what centres a modal dialog in the top layer. The
        // browser's own stylesheet says so, but Tailwind's reset zeroes every
        // margin first, which leaves the dialog pinned to the top-left corner.
        `m-auto max-h-[85vh] w-full overflow-y-auto ${
          wide ? "max-w-3xl" : "max-w-lg"
        } rounded-xl border border-neutral-200 bg-white p-5 shadow-xl backdrop:bg-neutral-900/40`
      }
    >
      <div className="mb-4 flex items-center justify-between gap-4">
        <h3 className="font-[family-name:--font-display] text-base font-semibold tracking-tight text-neutral-900">
          {title}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
        >
          ×
        </button>
      </div>
      {children}
    </dialog>
  );
}

export function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode | undefined;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      <div className="mb-3 flex items-center justify-between gap-4">
        <h2 className="font-[family-name:--font-display] text-base font-semibold tracking-tight text-neutral-900">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * Sort/filter state for one table, plus the row array to render. Filtering runs
 * before sorting so a search query narrows what gets ordered. `sorters` maps a
 * key (matched against `Th`'s `sortKey`) to an accessor returning the value to
 * compare — string compare handles numbers fine since we don't coerce, but
 * accessors can return numbers/Dates directly for correct ordering.
 */
export function useTable<T>(
  rows: T[],
  opts?: {
    filter?: (row: T, query: string) => boolean;
    sorters?: Record<string, (row: T) => string | number | Date>;
    initialSort?: string;
  },
) {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(opts?.initialSort ?? null);
  const [dir, setDir] = useState<"asc" | "desc">("asc");

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q || !opts?.filter) return rows;
    return rows.filter((row) => opts.filter!(row, q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, query]);

  const sorted = useMemo(() => {
    const get = sortKey ? opts?.sorters?.[sortKey] : undefined;
    if (!get) return filtered;
    const decorated = filtered.map((row) => ({ row, key: get(row) }));
    decorated.sort((a, b) => {
      if (a.key < b.key) return dir === "asc" ? -1 : 1;
      if (a.key > b.key) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return decorated.map((d) => d.row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sortKey, dir]);

  function toggleSort(key: string) {
    if (sortKey === key) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setDir("asc");
    }
  }

  return { rows: sorted, query, setQuery, sortKey, dir, toggleSort };
}

export function TableSearch({
  value,
  onChange,
  placeholder = "Filter…",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
}) {
  return (
    <input
      type="search"
      className={`${input} mb-2 max-w-xs`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

/**
 * `maxHeight` caps the body at a scrollable height with a sticky header, so a
 * long table doesn't push the rest of the page below the fold. Pass `false`
 * for tables that are always short (e.g. a handful of milestone rows).
 */
export function Table({
  head,
  children,
  maxHeight = "28rem",
}: {
  head: React.ReactNode;
  children: React.ReactNode;
  maxHeight?: string | false | undefined;
}) {
  return (
    <div
      className="overflow-auto rounded-xl border border-neutral-200 bg-white shadow-sm"
      style={maxHeight ? { maxHeight } : undefined}
    >
      <table className="w-full min-w-[36rem] text-sm">
        <thead className="sticky top-0 z-10 bg-neutral-50/90 backdrop-blur">
          <tr className="border-b border-neutral-200 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {head}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Th({
  children,
  right,
  sortKey,
  active,
  dir,
  onSort,
}: {
  children?: React.ReactNode;
  right?: boolean | undefined;
  sortKey?: string | undefined;
  active?: boolean | undefined;
  dir?: "asc" | "desc" | undefined;
  onSort?: ((key: string) => void) | undefined;
}) {
  if (!sortKey || !onSort) {
    return (
      <th className={`px-4 py-2.5 first:pl-4 font-normal ${right ? "text-right" : ""}`}>
        {children}
      </th>
    );
  }
  return (
    <th className={`px-4 py-2.5 first:pl-4 font-normal ${right ? "text-right" : ""}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={[
          "inline-flex items-center gap-1 hover:text-neutral-900",
          right ? "flex-row-reverse" : "",
          active ? "text-neutral-900" : "",
        ].join(" ")}
      >
        {children}
        <span className="text-[9px] leading-none">
          {active ? (dir === "asc" ? "▲" : "▼") : "⇅"}
        </span>
      </button>
    </th>
  );
}

/**
 * A sortable `Th` with one table's state already bound.
 *
 * `Th` takes the state apart field by field, which is fine for a page with one
 * table and unreadable on a page with seven.
 */
export function SortTh({
  table,
  sortKey,
  right,
  children,
}: {
  table: Pick<ReturnType<typeof useTable>, "sortKey" | "dir" | "toggleSort">;
  sortKey: string;
  right?: boolean | undefined;
  children?: React.ReactNode;
}) {
  return (
    <Th
      sortKey={sortKey}
      right={right}
      active={table.sortKey === sortKey}
      dir={table.dir}
      onSort={table.toggleSort}
    >
      {children}
    </Th>
  );
}

export function Td({
  children,
  right,
  mono,
  muted,
  colSpan,
}: {
  children?: React.ReactNode;
  right?: boolean | undefined;
  mono?: boolean | undefined;
  muted?: boolean | undefined;
  colSpan?: number | undefined;
}) {
  return (
    <td
      colSpan={colSpan}
      className={[
        "px-4 py-2.5 align-middle first:pl-4",
        right ? "text-right" : "",
        mono ? "tabular" : "",
        muted ? "text-neutral-500" : "",
      ].join(" ")}
    >
      {children}
    </td>
  );
}

/**
 * One headline figure. Negative amounts are the ones worth spotting from across
 * the room, so they colour themselves rather than relying on every caller.
 */
export function Stat({
  label,
  value,
  currency,
  hint,
  emphasis,
}: {
  label: string;
  value: string;
  currency?: string | undefined;
  hint?: string | undefined;
  emphasis?: boolean | undefined;
}) {
  return (
    <div
      className={`rounded-xl border bg-white p-3.5 shadow-sm transition-shadow hover:shadow-md ${
        emphasis ? "border-accent-500 ring-1 ring-accent-500/20" : "border-neutral-200"
      }`}
    >
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</div>
      <div
        className={`mt-1 tabular text-lg font-semibold ${value.startsWith("-") ? "text-red-600" : "text-neutral-900"}`}
      >
        {value} {currency && <span className="text-sm text-neutral-400">{currency}</span>}
      </div>
      {hint && <div className="mt-0.5 text-xs text-neutral-400">{hint}</div>}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50/50 p-8 text-center">
      <p className="font-medium text-neutral-700">{title}</p>
      {children && <p className="mt-1 text-sm text-neutral-500">{children}</p>}
    </div>
  );
}

export function ErrorNote({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="mb-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700"
    >
      {children}
    </p>
  );
}

export function Loading() {
  return (
    <p className="flex items-center gap-2 text-sm text-neutral-500">
      <span className="h-3 w-3 animate-spin rounded-full border-2 border-neutral-300 border-t-accent-500" />
      Loading…
    </p>
  );
}
